import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["subMenu"];
import { useCallback, useState } from 'react';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
// @ts-expect-error Untyped module
import ToolbarMenu from 'rich-text-lib/components/toolbar/ToolbarMenu';
import styled from 'styled-components';
import UIDropdownDivider from 'UIComponents/dropdown/UIDropdownDivider';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILink from 'UIComponents/link/UILink';
import UIToolTip from 'UIComponents/tooltip/UITooltip';
import UITextToolbarButton from 'rich-text-lib/components/editor/UITextToolbarButton';
import { ADD_AI_IMAGE_COMMAND, SUPER_ADMIN_SCOPE } from '../constants/hscontentgeneration';
import { hasScope } from '../utils/settings';
import { trackInteraction } from '../utils/usageTracking';
// @ts-expect-error Untyped module
import { handleOpenCAGenerationModal } from '../hscontentgeneration/generative';
import { CONTENT_GENERATION_CLICK_SETTINGS_LINK, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE, CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION } from '../constants/usageTracking';
import { resetAutocompleteAtomState, updateAutocompleteActiveRange, useIsAutocompleteResultPopoverOpen
// @ts-expect-error Untyped module
} from '../data/autocompleteAtoms';
import { getAtomValue } from '../data/atomUtils';
import { hsContentAssistanceSelectedCommandAtom } from '../data/contentAssistanceAtoms';
import { insertNodeAtEndOfHighlightedText } from '../hscontentgeneration/utils';
import { getAutocompleteSpan, updateAutocompleteActiveRangeAndSetSpan
// @ts-expect-error Untyped module
} from '../hsautocomplete/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSettingsLink = editor => {
  return `${editor.settings.appUrl}/settings/${PortalIdParser.get()}/ai-assistants`;
};
const getBrandVoiceLink = (editor, brandKitId) => {
  return `${editor.settings.appUrl}/settings/${PortalIdParser.get()}/account-defaults/branding/brand-kit/${brandKitId || 'primary'}/brand-voice`;
};
const getIsSuperAdmin = editor => {
  const {
    scopes
  } = editor.settings;
  const isSuperAdmin = hasScope(SUPER_ADMIN_SCOPE, scopes);
  return isSuperAdmin;
};
const getIsHighlightingText = editor => {
  return editor.selection && !editor.selection.isCollapsed();
};
const TooltipText = ({
  editor,
  isDisabled,
  isSuperAdmin
}) => {
  if (!isDisabled) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "TinymcePlugins.hscontentgeneration.menuItem"
    });
  }
  // Disabled and a super admin - show the settings link
  if (isSuperAdmin) {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: "TinymcePlugins.hscontentgeneration.toolbar.buttonDisabled.superAdmin_jsx",
      elements: {
        UILink
      },
      options: {
        onClick: () => {
          trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_CLICK_SETTINGS_LINK);
          window.open(getSettingsLink(editor));
        },
        external: true
      }
    });
  }
  // Disabled for non-super admin
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "TinymcePlugins.hscontentgeneration.toolbar.buttonDisabled.default"
  });
};
const GenerateLabel = styled.div.withConfig({
  displayName: "ContentAssistantSlashAndSelectionCommandsToolbarManager__GenerateLabel",
  componentId: "jf1zgx-0"
})(["background-color:", ";padding:8px 20px;font-weight:500;pointer-events:none;border-radius:3px 3px 0;"], GYPSUM);
export function getSlashCommandOptionsForTinyMCEToolbar(editor, slashCommands) {
  const slashCommandOptions = slashCommands.map(command => {
    return {
      name: command.commandId || command.id,
      onClick: () => {
        if (command.id === ADD_AI_IMAGE_COMMAND.id) {
          editor.fire('hsimage:openGeneratePanel');
        } else {
          handleOpenCAGenerationModal(editor, command);
        }
        trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
          actionType: CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE,
          selected: command.commandId || command.id
        });
      },
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: command.labelKey
      })
    };
  });
  return [{
    render: () => /*#__PURE__*/_jsxs(GenerateLabel, {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "artificialIntelligence",
        className: "m-right-1",
        style: {
          marginLeft: -4
        }
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "TinymcePlugins.hscontentgeneration.toolbar.generateLabel"
      })]
    })
  }, ...slashCommandOptions];
}
function maybeGetToolipTitle(formattedCommand, editor, brandVoiceOptions) {
  if (!formattedCommand.disabled || formattedCommand.labelKey !== 'baseContentGenerationModal.inlineToolbar.options.brandVoice') {
    return undefined;
  }
  const {
    brandKitId,
    isSetUp = false,
    isEnabledForApp = false
  } = brandVoiceOptions || {};
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: isSetUp && !isEnabledForApp ? 'TinymcePlugins.hscontentgeneration.brandVoiceDisabledForAppTooltip_jsx' : 'TinymcePlugins.hscontentgeneration.brandVoiceNotSetUpTooltip_jsx',
    elements: {
      UILink
    },
    options: {
      // For some reason href was not opening the link when clicked,
      // so had to manually open link with window.open for now
      onClick: () => window.open(getBrandVoiceLink(editor, brandKitId), '_blank'),
      external: true
    }
  });
}
function getSelectionCommandOptions(editor, selectionCommands, brandVoiceOptions) {
  const selectionCommandOptions = selectionCommands.map(command => {
    const {
        subMenu
      } = command,
      rest = _objectWithoutPropertiesLoose(command, _excluded);
    const formattedCommand = Object.assign({}, rest, {
      shouldFetchContentOnModalOpen: true
    });
    if (subMenu) {
      return {
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: formattedCommand.labelKey
        }),
        name: formattedCommand.commandId,
        child: subMenu.map(subMenuOption => {
          // Add the subPrompt
          formattedCommand.parameters = subMenuOption.parameters;
          const toneOfVoice = subMenuOption.parameters.toneOfVoice;
          return {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: subMenuOption.labelKey
            }),
            name: toneOfVoice,
            onClick: () => {
              handleOpenCAGenerationModal(editor, formattedCommand);
              trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
                actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE,
                selected: formattedCommand.commandId,
                choice: toneOfVoice
              });
            }
          };
        })
      };
    }
    const tooltipTitle = maybeGetToolipTitle(formattedCommand, editor, brandVoiceOptions);
    const text = formattedCommand.disabled && tooltipTitle ? /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: formattedCommand.labelKey
      }), /*#__PURE__*/_jsx(UIHelpIcon, {
        className: "m-left-1",
        title: tooltipTitle,
        tooltipPlacement: "right"
      })]
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: formattedCommand.labelKey
    });
    const {
      isSetUp = false,
      isEnabledForApp = false
    } = brandVoiceOptions || {};
    return {
      disabled: formattedCommand.disabled,
      text,
      name: formattedCommand.commandId,
      onClick: () => {
        handleOpenCAGenerationModal(editor, formattedCommand);
        trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_TOOLBAR_MENU_SELECTION, {
          actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE,
          selected: formattedCommand.commandId,
          usingBrandVoice: isSetUp && isEnabledForApp
        });
      }
    };
  });
  return selectionCommandOptions;
}
export const getToolbarMenuItems = ({
  brandVoiceOptions,
  editor,
  slashCommands,
  selectionCommands,
  isHighlightingText,
  isSuperAdmin
}) => {
  let commands;
  // Highlighted text commands
  if (isHighlightingText) {
    commands = getSelectionCommandOptions(editor, selectionCommands, brandVoiceOptions);
    // Slash command
  } else {
    commands = getSlashCommandOptionsForTinyMCEToolbar(editor, slashCommands);
  }
  // For none super admins - return the commands
  if (!isSuperAdmin) {
    return commands;
  }
  // Only Add settings link for super-admins!
  // Add divider
  commands.push({
    render: UIDropdownDivider
  });
  const handleOnClickLink = () => {
    trackInteraction(editor, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_CLICK_SETTINGS_LINK, {
      // Track what type of menu was open
      actionType: isHighlightingText ? CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE : CONTENT_GENERATION_SLASH_COMMAND_ACTION_TYPE
    });
  };
  // Add settings link
  commands.push({
    name: 'settings',
    icon: 'settings',
    text: /*#__PURE__*/_jsx(UILink, {
      external: true,
      onClick: handleOnClickLink,
      href: getSettingsLink(editor),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "TinymcePlugins.hscontentgeneration.toolbar.settings"
      })
    })
  });
  return commands;
};
export const ToolbarDropdownButton = ({
  brandVoiceOptions,
  editor,
  isDisabled,
  slashCommands,
  selectionCommands
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const isAnyAutocompletePopoverOpen = useIsAutocompleteResultPopoverOpen(editor);
  const isSuperAdmin = getIsSuperAdmin(editor);
  const handleOnOpenChange = useCallback(() => {
    const isHighlightingText = getIsHighlightingText(editor);
    const range = editor.selection.getRng().cloneRange();
    const selectedCommand = getAtomValue(hsContentAssistanceSelectedCommandAtom);
    if (!isOpen) {
      // Create a new menu every time it is opened - important as editorContext / selection may have changed
      setMenuItems(getToolbarMenuItems({
        brandVoiceOptions,
        editor,
        slashCommands,
        selectionCommands,
        isHighlightingText,
        isSuperAdmin
      }));
      // If a autocomplete popover is currently open, close it
      if (isAnyAutocompletePopoverOpen) {
        resetAutocompleteAtomState();
      }
      if (isHighlightingText) {
        // autocomplete span is what is used to place the tooltip
        insertNodeAtEndOfHighlightedText(editor, getAutocompleteSpan());
        // active range is required for replacing the text on insertion
        updateAutocompleteActiveRange(range);
      } else {
        // Setup the range, and add the spans, so the modal will have a place to live when it opens
        updateAutocompleteActiveRangeAndSetSpan(editor, range);
      }
    }
    // If there is no selection when it closes, cleanup the Autocomplete atoms
    if (isOpen && !selectedCommand) {
      resetAutocompleteAtomState();
    }
    setIsOpen(!isOpen);
  }, [brandVoiceOptions, editor, isAnyAutocompletePopoverOpen, isOpen, isSuperAdmin, selectionCommands, slashCommands]);
  const isHighlightingText = getIsHighlightingText(editor);
  return /*#__PURE__*/_jsx(UIToolTip, {
    placement: "bottom",
    disabled: isOpen,
    title: /*#__PURE__*/_jsx(TooltipText, {
      editor: editor,
      isDisabled: isDisabled,
      isSuperAdmin: isSuperAdmin
    }),
    children: /*#__PURE__*/_jsx(UITextToolbarButton, {
      selected: isOpen,
      children: /*#__PURE__*/_jsx(ToolbarMenu, {
        buttonClassName: "mce-content-assistant-button",
        "data-test-id": "content-assistant-button",
        iconName: "artificialIntelligence",
        onOpenChange: handleOnOpenChange,
        dropdownClassName: `mce-hubspot-menu mce-content-assistant-menu ${isHighlightingText ? 'mce-ca-select' : 'mce-ca-slash'}`,
        menuItems: menuItems,
        disabled: isDisabled,
        style: {
          margin: 0,
          padding: 0
        },
        popoverProps: {
          arrowColor: isHighlightingText ? OLAF : GYPSUM
        }
      })
    })
  });
};